import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Partner, CreatePartnerRequest, UpdatePartnerRequest, PartnersQueryParams } from '../app/models/partner';
import { Connection, CreateConnectionRequest } from '../app/models/connection';
import { CreateTenantRequest, Tenant, FeatureFlagCollection, FeatureFlagDTO, UpdateFeatureFlagRequest, SmsCredit, SmsCreditLedgerEntry, ChangeSmsCreditRequest, StorageUsage } from '../app/models/tenant';
import { AdminUserCollection, AdminUserCreateRequest, AdminUserCreatedResponse, InvitationCollection, InvitationDTO } from '../app/models/user';
import { ShipmentsQueryParams, Shipment } from '../app/models/shipment';
import { PaginatedCollection } from '../app/models/PaginatedCollection';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PartnersService {
  private readonly apiUrl = `${environment.api.baseUrl}${environment.api.endpoints.partners}`;
  private readonly tenantsUrl = `${environment.api.baseUrl}${environment.api.endpoints.tenants}`;

  constructor(private http: HttpClient) {}

  getPartners(params: PartnersQueryParams = {}): Observable<PaginatedCollection<Partner>> {
    const queryParams = new URLSearchParams();

    if (params.type && params.type.trim() !== '') {
      queryParams.set('type', params.type);
    }

    if (params.page !== undefined) {
      queryParams.set('page', params.page.toString());
    }

    if (params.size !== undefined) {
      queryParams.set('size', params.size.toString());
    }

    if (params.sort && params.sort.length > 0) {
      params.sort.forEach(sortParam => {
        queryParams.append('sort', sortParam);
      });
    }

    if (params.searchTerm && params.searchTerm.trim() !== '') {
      queryParams.set('searchTerm', params.searchTerm.trim());
    }

    // Explicitly against undefined, not truthiness: isTenant === false is a real filter
    // ("non-tenants"), and a truthy check would drop it and silently list everything.
    if (params.isTenant !== undefined) {
      queryParams.set('isTenant', String(params.isTenant));
    }

    const url = queryParams.toString() ? `${this.apiUrl}?${queryParams.toString()}` : this.apiUrl;
    return this.http.get<PaginatedCollection<Partner>>(url);
  }

  createPartner(partner: CreatePartnerRequest): Observable<Partner> {
    return this.http.post<Partner>(this.apiUrl, partner);
  }

  updatePartner(id: string, partner: UpdatePartnerRequest): Observable<Partner> {
    return this.http.put<Partner>(`${this.apiUrl}/${id}`, partner);
  }

  getPartnerById(id: string): Observable<Partner> {
    return this.http.get<Partner>(`${this.apiUrl}/${id}`);
  }

  getPartnerConnections(id: string): Observable<PaginatedCollection<Partner>> {
    return this.http.get<PaginatedCollection<Partner>>(`${this.apiUrl}/${id}/connections`);
  }

  addPartnerConnection(partnerId: string, connectionData: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${partnerId}/connections`, connectionData);
  }

  createTenant(createTenantData: CreateTenantRequest): Observable<Tenant> {
    return this.http.post<Tenant>(this.tenantsUrl, createTenantData);
  }

  getTenantFeatureFlags(tenantId: string | number): Observable<FeatureFlagCollection> {
    return this.http.get<FeatureFlagCollection>(`${this.tenantsUrl}/${tenantId}/feature-flags`);
  }

  updateTenantFeatureFlag(tenantId: string | number, body: UpdateFeatureFlagRequest): Observable<FeatureFlagDTO> {
    return this.http.put<FeatureFlagDTO>(`${this.tenantsUrl}/${tenantId}/feature-flags`, body);
  }

  getStorage(tenantId: string | number): Observable<StorageUsage> {
    return this.http.get<StorageUsage>(`${this.tenantsUrl}/${tenantId}/storage`);
  }

  getSmsCredit(tenantId: string | number): Observable<SmsCredit> {
    return this.http.get<SmsCredit>(`${this.tenantsUrl}/${tenantId}/sms-credits`);
  }

  getSmsCreditLedger(tenantId: string | number): Observable<{ items: SmsCreditLedgerEntry[] }> {
    return this.http.get<{ items: SmsCreditLedgerEntry[] }>(`${this.tenantsUrl}/${tenantId}/sms-credits/ledger`);
  }

  changeSmsCredit(tenantId: string | number, body: ChangeSmsCreditRequest): Observable<SmsCredit> {
    return this.http.post<SmsCredit>(`${this.tenantsUrl}/${tenantId}/sms-credits`, body);
  }

  /** The invitations a tenant is waiting on, for the users tab. */
  getPartnerInvitations(groupId: string | number): Observable<InvitationCollection> {
    const url = `${environment.api.baseUrl}${environment.api.endpoints.users}/invitations?groupId=${groupId}`;
    return this.http.get<InvitationCollection>(url);
  }

  /** Sends one again on a fresh link; the previous one stops working. */
  resendPartnerInvitation(groupId: string | number, invitationId: number): Observable<InvitationDTO> {
    const url = `${environment.api.baseUrl}${environment.api.endpoints.users}/invitations/${invitationId}/resend?groupId=${groupId}`;
    return this.http.post<InvitationDTO>(url, {});
  }

  /** Withdraws it, and with it the seat it was holding. */
  withdrawPartnerInvitation(groupId: string | number, invitationId: number): Observable<void> {
    const url = `${environment.api.baseUrl}${environment.api.endpoints.users}/invitations/${invitationId}?groupId=${groupId}`;
    return this.http.delete<void>(url);
  }

  getPartnerUsers(groupId: string | number): Observable<AdminUserCollection> {
    const usersUrl = `${environment.api.baseUrl}${environment.api.endpoints.users}?groupId=${groupId}`;
    return this.http.get<AdminUserCollection>(usersUrl);
  }

  /**
   * Provisions a Keycloak account into a tenant. The response carries a one-time temporary
   * password that is not retrievable afterwards, so the caller must surface it right away.
   */
  createPartnerUser(request: AdminUserCreateRequest): Observable<AdminUserCreatedResponse> {
    const usersUrl = `${environment.api.baseUrl}${environment.api.endpoints.users}`;
    return this.http.post<AdminUserCreatedResponse>(usersUrl, request);
  }

  getPartnerShipments(params: ShipmentsQueryParams): Observable<PaginatedCollection<Shipment>> {
    const shipmentsUrl = `${environment.api.baseUrl}${environment.api.endpoints.shipments}`;
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.size !== undefined) queryParams.set('size', params.size.toString());
    if (params.sort) params.sort.forEach(sort => queryParams.append('sort', sort));
    if (params.partnerId !== undefined) queryParams.set('partnerId', params.partnerId.toString());

    const url = `${shipmentsUrl}?${queryParams.toString()}`;
    return this.http.get<PaginatedCollection<Shipment>>(url);
  }

  /**
   * The logo URL is a presigned S3 GET, so it carries its own credentials in the query string. The
   * auth interceptor only adds the bearer token to our API; S3 would reject a request with both.
   */
  downloadImage(imageUrl: string): Observable<Blob> {
    return this.http.get(imageUrl, { responseType: 'blob' });
  }
}
